export enum PredefinedPackage {
  None = 0,
  WebLite1 = 1,
  Web5 = 2,
  Web20 = 3,
  Web50 = 4,
  Web100 = 5,
  Web250 = 6,
  WebPro = 7,
}

export const getPredefinedPackageSize = (
  predefinedPackage: PredefinedPackage,
  activeDriversLimit: number,
): number => {
  let packageSize = 0

  if (predefinedPackage == null) {
    predefinedPackage = getClosestPackage(activeDriversLimit)
  }

  switch (predefinedPackage) {
    case PredefinedPackage.WebLite1:
      packageSize = 1
      break
    case PredefinedPackage.Web5:
      packageSize = 5
      break
    case PredefinedPackage.Web20:
      packageSize = 20
      break
    case PredefinedPackage.Web50:
      packageSize = 50
      break
    case PredefinedPackage.Web100:
      packageSize = 100
      break
    case PredefinedPackage.Web250:
      packageSize = 250
      break
    case PredefinedPackage.WebPro:
      packageSize = 250
      break
  }

  return packageSize
}

export const getPredefinedPackageByNumber = (
  ppackage: number,
): PredefinedPackage => {
  let predefinedPackage = PredefinedPackage.None

  switch (ppackage) {
    case 1:
      predefinedPackage = PredefinedPackage.WebLite1
      break
    case 2:
      predefinedPackage = PredefinedPackage.Web5
      break
    case 3:
      predefinedPackage = PredefinedPackage.Web20
      break
    case 4:
      predefinedPackage = PredefinedPackage.Web50
      break
    case 5:
      predefinedPackage = PredefinedPackage.Web100
      break
    case 6:
      predefinedPackage = PredefinedPackage.Web250
      break
    case 7:
      predefinedPackage = PredefinedPackage.WebPro
      break
  }

  return predefinedPackage
}

export const getAdditionalAmount = (
  predefinedPackage: PredefinedPackage,
  packageMultiplier: number,
  activeDriversLimit: number,
  driversCount: number = null,
  discount: boolean = false,
  discountValue: number = 0,
): number => {
  if (packageMultiplier == null || packageMultiplier == 0) {
    packageMultiplier = 1
  }

  if (predefinedPackage != null) {
    const packageSize =
      getPredefinedPackageSize(predefinedPackage, activeDriversLimit) *
      packageMultiplier
    const baseAmount =
      driversCount != null
        ? 0
        : getBaseAmount(predefinedPackage, discount, discountValue) *
          packageMultiplier

    if (driversCount == null || driversCount <= packageSize) {
      return baseAmount
    } else {
      return (
        baseAmount +
        (driversCount - packageSize) *
          getAmountPerDriver(
            predefinedPackage,
            activeDriversLimit,
            discount,
            discountValue,
          )
      )
    }
  } else if (activeDriversLimit) {
    const closestPackage = getClosestPackage(activeDriversLimit)
    const closestPackageSize = getPredefinedPackageSize(closestPackage, null)

    let multiplier = 1
    if (activeDriversLimit > closestPackageSize) {
      multiplier = Math.floor(activeDriversLimit / closestPackageSize)
    }
    const packageSize = activeDriversLimit
    let baseAmount = 0

    if (driversCount == null) {
      if (activeDriversLimit <= closestPackageSize) {
        baseAmount = getBaseAmount(closestPackage, discount, discountValue)
      } else {
        baseAmount =
          multiplier * getBaseAmount(closestPackage, discount, discountValue)

        const over = activeDriversLimit % closestPackageSize
        if (over > 0) {
          baseAmount +=
            over *
            getAmountPerDriver(
              closestPackage,
              activeDriversLimit,
              discount,
              discountValue,
              true,
            )
        }
      }
    }

    if (driversCount == null || driversCount <= packageSize) {
      return baseAmount
    } else {
      return (
        baseAmount +
        (driversCount - packageSize) *
          getAmountPerDriver(
            closestPackage,
            activeDriversLimit,
            discount,
            discountValue,
          )
      )
    }
  } else {
    return 0
  }
}

export const getBaseAmount = (
  predefinedPackage: PredefinedPackage,
  discount: boolean,
  discountValue: number,
): number => {
  let amount = 0
  switch (predefinedPackage) {
    case PredefinedPackage.WebLite1:
      amount = 10
      break
    case PredefinedPackage.Web5:
      amount = discount ? getDiscount(250, discountValue) : 250
      break
    case PredefinedPackage.Web20:
      amount = discount ? getDiscount(800, discountValue) : 800
      break
    case PredefinedPackage.Web50:
      amount = discount ? getDiscount(1250, discountValue) : 1250
      break
    case PredefinedPackage.Web100:
      amount = discount ? getDiscount(1500, discountValue) : 1500
      break
    case PredefinedPackage.Web250:
      amount = discount ? getDiscount(1875, discountValue) : 1875
      break
    case PredefinedPackage.WebPro:
      amount = discount ? getDiscount(1875, discountValue) : 1875
      break
  }

  return amount
}

export const getDiscount = (
  baseValue: number,
  discountValue: number,
): number => {
  return ((100 - discountValue) / 100) * baseValue
}

export const getAmountPerDriver = (
  predefinedPackage: PredefinedPackage,
  activeDriversLimit: number,
  discount: boolean,
  discountValue: number,
  override250: boolean = false,
): number => {
  if (predefinedPackage == null) {
    predefinedPackage = getClosestPackage(activeDriversLimit)
  }

  let amount = 0
  switch (predefinedPackage) {
    case PredefinedPackage.WebLite1:
      amount = discount ? getDiscount(10, discountValue) : 10
      break
    case PredefinedPackage.Web5:
      amount = discount ? getDiscount(50, discountValue) : 50
      break
    case PredefinedPackage.Web20:
      amount = discount ? getDiscount(40, discountValue) : 40
      break
    case PredefinedPackage.Web50:
      amount = discount ? getDiscount(25, discountValue) : 25
      break
    case PredefinedPackage.Web100:
      amount = discount ? getDiscount(15, discountValue) : 15
      break
    case PredefinedPackage.Web250:
      amount = discount
        ? getDiscount(override250 ? 7.5 : 8, discountValue)
        : override250
          ? 7.5
          : 8
      break
    case PredefinedPackage.WebPro:
      amount = discount
        ? getDiscount(override250 ? 7.5 : 8, discountValue)
        : override250
          ? 7.5
          : 8
      break
  }

  return amount
}

export const getAmountPerDriverInPackage = (
  predefinedPackage: PredefinedPackage,
  activeDriversLimit: number,
): number => {
  if (predefinedPackage == null) {
    predefinedPackage = getClosestPackage(activeDriversLimit)
  }

  let amount = 0
  switch (predefinedPackage) {
    case PredefinedPackage.WebLite1:
      amount = 10
      break
    case PredefinedPackage.Web5:
      amount = 50
      break
    case PredefinedPackage.Web20:
      amount = 40
      break
    case PredefinedPackage.Web50:
      amount = 25
      break
    case PredefinedPackage.Web100:
      amount = 15
      break
    case PredefinedPackage.Web250:
      amount = 8
      break
  }

  return amount
}

export const getClosestPackage = (
  activeDriversLimit: number,
): PredefinedPackage => {
  if (activeDriversLimit <= 1) {
    return PredefinedPackage.WebLite1
  } else if (activeDriversLimit > 1 && activeDriversLimit < 17) {
    return PredefinedPackage.Web5
  } else if (activeDriversLimit >= 17 && activeDriversLimit < 32) {
    return PredefinedPackage.Web20
  } else if (activeDriversLimit >= 32 && activeDriversLimit < 61) {
    return PredefinedPackage.Web50
  } else if (activeDriversLimit >= 61 && activeDriversLimit < 126) {
    return PredefinedPackage.Web100
  } else {
    return PredefinedPackage.Web250
  }
}
